import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { addDays, toDateKey } from '../utils/dates';
import { coverTypeStyle, isCoverColour } from '../utils/coverTypeColour';
import './OnCall.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu'];

function canAdmin(role) {
  return ['team_leader', 'manager', 'admin'].includes(role);
}

// Keys the UTC-midnight days getCalendarRange builds. Not for a local Date
// such as new Date(): today's key comes from toDateKey.
function dateKey(date) {
  return date.toISOString().slice(0, 10);
}

function getCalendarRange(year, month) {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const last = new Date(Date.UTC(year, month, 0));
  const start = new Date(first);
  const offset = (first.getUTCDay() - 5 + 7) % 7;
  start.setUTCDate(first.getUTCDate() - offset);
  const totalDays = offset + last.getUTCDate();
  const weeks = Math.ceil(totalDays / 7);
  const days = Array.from({ length: weeks * 7 }, (_, index) => {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + index);
    return day;
  });
  return { start, days, count: days.length };
}

function OnCallAdmin() {
  const { authFetch, user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [rota, setRota] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const [coverTypes, setCoverTypes] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [gapStart, setGapStart] = useState(() => toDateKey(new Date()));
  const [gapEnd, setGapEnd] = useState(() => toDateKey(addDays(new Date(), 6)));
  const [gapWarnings, setGapWarnings] = useState([]);
  const [coverForm, setCoverForm] = useState({ label: '', color: '#1D9E75', display_order: 100 });
  const [blockForm, setBlockForm] = useState({ start_date: toDateKey(new Date()), end_date: toDateKey(new Date()), cover_type_id: '', engineer_id: '', notes: '' });
  const [adminSwapForm, setAdminSwapForm] = useState({ assignment_id: '', proposed_engineer_id: '', reason: '' });
  const [savingAdminSwap, setSavingAdminSwap] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [dayForm, setDayForm] = useState({});
  const [message, setMessage] = useState(null);
  const role = user?.role || 'engineering';

  const fetchData = useCallback(async () => {
    const { start, count } = getCalendarRange(year, month);
    const params = new URLSearchParams({ view: 'list', start: dateKey(start), days: String(count) });
    const [rotaRes, engineersRes, coverRes, swapsRes, auditRes] = await Promise.all([
      authFetch(`/api/v1/on-call/rota?${params}`),
      authFetch('/api/v1/on-call/engineers'),
      authFetch('/api/v1/on-call/admin/cover-types'),
      authFetch('/api/v1/on-call/swap-requests'),
      authFetch('/api/v1/on-call/admin/audit-log')
    ]);
    // A failed rota fetch must never render as a silently empty calendar
    if (rotaRes.ok) {
      setRota(await rotaRes.json());
    } else {
      setRota(null);
      setMessage({ type: 'error', text: 'The rota could not be loaded - please try again. Assignments may still have saved.' });
    }
    if (engineersRes.ok) setEngineers((await engineersRes.json()).data || []);
    if (coverRes.ok) {
      const types = (await coverRes.json()).data || [];
      setCoverTypes(types);
      // The block form only offers active types, so its selection must come
      // from them too - a retired Main Cover would otherwise sit selected but
      // unlisted, and the form would post a retired id.
      const active = types.filter(t => t.is_active !== false);
      setBlockForm(prev => active.some(t => t.cover_type_id === prev.cover_type_id)
        ? prev
        : { ...prev, cover_type_id: active.find(t => t.code === 'main_cover')?.cover_type_id || active[0]?.cover_type_id || '' });
    }
    if (swapsRes.ok) setSwapRequests(((await swapsRes.json()).data || []).filter(request => request.status === 'pending'));
    if (auditRes.ok) {
      const auditPayload = await auditRes.json();
      setAuditLog(auditPayload.data || []);
      setAuditTotal(auditPayload.total || (auditPayload.data || []).length);
    }
  }, [authFetch, year, month]);

  const loadMoreAudit = useCallback(async () => {
    const response = await authFetch(`/api/v1/on-call/admin/audit-log?offset=${auditLog.length}`);
    if (response.ok) {
      const payload = await response.json();
      setAuditLog(prev => [...prev, ...(payload.data || [])]);
      setAuditTotal(payload.total || 0);
    }
  }, [authFetch, auditLog.length]);

  useEffect(() => {
    if (canAdmin(role)) fetchData();
  }, [fetchData, role]);

  const daysByDate = useMemo(() => rota?.days || {}, [rota]);
  const warningsByDate = useMemo(() => {
    const result = {};
    (rota?.warnings || []).forEach(day => { result[day.date] = day.warnings; });
    return result;
  }, [rota]);
  const assignedSwapOptions = useMemo(() => (
    Object.entries(daysByDate).flatMap(([date, assignments]) => (
      assignments
        .filter(assignment => assignment.engineer_id)
        .map(assignment => ({
          ...assignment,
          date,
          label: `${date} - ${assignment.cover_type_label} - ${assignment.engineer_name}`
        }))
    ))
  ), [daysByDate]);
  const selectedAdminSwapAssignment = assignedSwapOptions.find(option => option.assignment_id === adminSwapForm.assignment_id);

  if (!canAdmin(role)) return <Navigate to="/on-call" replace />;

  const createBlankYear = async () => {
    const response = await authFetch('/api/v1/on-call/admin/blank-year', {
      method: 'POST',
      body: JSON.stringify({ year })
    });
    const data = await response.json();
    setMessage(data.message || (response.ok ? 'Blank year created' : 'Unable to create blank year'));
    fetchData();
  };

  const applyBlockAssignment = async (event) => {
    event.preventDefault();
    const response = await authFetch('/api/v1/on-call/admin/block-assign', {
      method: 'POST',
      body: JSON.stringify({
        ...blockForm,
        engineer_id: blockForm.engineer_id || null,
        notes: blockForm.notes || null
      })
    });
    const data = await response.json();
    setMessage(data.message || (response.ok ? 'Block assignment applied' : 'Unable to apply block assignment'));
    fetchData();
  };

  const openDayEditor = (date) => {
    const key = dateKey(date);
    const values = {};
    (daysByDate[key] || []).forEach(item => {
      values[item.assignment_id] = { engineer_id: item.engineer_id || '', notes: item.notes || '' };
    });
    setEditingDay({ date: key, assignments: daysByDate[key] || [] });
    setDayForm(values);
  };

  const saveDayEditor = async () => {
    for (const assignment of editingDay.assignments) {
      const values = dayForm[assignment.assignment_id] || {};
      await authFetch(`/api/v1/on-call/admin/assignments/${assignment.assignment_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          engineer_id: values.engineer_id || null,
          notes: values.notes || null
        })
      });
    }
    setEditingDay(null);
    setMessage(`Updated rota for ${editingDay.date}`);
    fetchData();
  };

  const reviewSwap = async (request, status) => {
    const reviewNote = window.prompt(status === 'approved' ? 'Approval note' : 'Rejection reason') || '';
    const response = await authFetch(`/api/v1/on-call/admin/swap-requests/${request.request_id}`, {
      method: 'PUT',
      body: JSON.stringify({ status, review_note: reviewNote })
    });
    const data = await response.json();
    setMessage(data.message || (response.ok ? `Swap ${status}` : 'Unable to review swap'));
    fetchData();
  };

  const createAdminSwapRequest = async (event) => {
    event.preventDefault();
    setSavingAdminSwap(true);
    try {
      const selectedAssignment = selectedAdminSwapAssignment;
      const proposedEngineer = engineers.find(engineer => engineer.user_id === adminSwapForm.proposed_engineer_id);
      const response = await authFetch('/api/v1/on-call/admin/swap-requests', {
        method: 'POST',
        body: JSON.stringify(adminSwapForm)
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Unable to create swap request');
      }

      setSwapRequests(prev => [
        {
          ...data,
          cover_type_label: selectedAssignment?.cover_type_label,
          requesting_engineer_name: selectedAssignment?.engineer_name,
          current_engineer_name: selectedAssignment?.engineer_name,
          proposed_engineer_name: proposedEngineer?.full_name,
          status: data.status || 'pending'
        },
        ...prev.filter(request => request.request_id !== data.request_id)
      ]);
      setAdminSwapForm({ assignment_id: '', proposed_engineer_id: '', reason: '' });
      setMessage(data.message || 'Swap request created for review');
      await fetchData();
    } catch (error) {
      setMessage(error.message || 'Unable to create swap request');
    } finally {
      setSavingAdminSwap(false);
    }
  };

  const reviewGaps = async (notify = false) => {
    const params = new URLSearchParams({ start: gapStart, end: gapEnd, notify: String(notify) });
    const response = await authFetch(`/api/v1/on-call/admin/gaps?${params}`);
    const data = await response.json();
    setGapWarnings(data.warnings || []);
    setMessage(notify ? 'Management/Scheduling group notified of rota warnings' : 'Rota gaps reviewed');
  };

  const createCoverType = async (event) => {
    event.preventDefault();
    const response = await authFetch('/api/v1/on-call/admin/cover-types', {
      method: 'POST',
      body: JSON.stringify(coverForm)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setMessage({ type: 'error', text: data.message || 'Unable to add cover type' });
      return;
    }
    setCoverForm({ label: '', color: '#1D9E75', display_order: 100 });
    setMessage('Cover type added');
    fetchData();
  };

  // Edit, retire and reactivate all go through the one PUT. The whole row is
  // sent every time, so the call is right whatever the API defaults to.
  const saveCoverType = async (id, body, successText) => {
    const response = await authFetch(`/api/v1/on-call/admin/cover-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setMessage({ type: 'error', text: data.message || 'Unable to update cover type' });
      return false;
    }
    setMessage(successText);
    fetchData();
    return true;
  };

  // Only the flag (the PUT needs the label and keeps whatever it is not sent),
  // so a stored colour the API would now refuse can never block a retire.
  const setCoverTypeActive = (type, isActive) => saveCoverType(
    type.cover_type_id,
    { label: type.label, is_active: isActive },
    `${type.label} ${isActive ? 'reactivated' : 'retired'}`
  );

  return (
    <div className="on-call-page on-call-admin-page">
      <div className="on-call-header">
        <div>
          <h1>On Call Rota Admin</h1>
          <p>Plan cover visually, assign blocks of dates, and review gaps before payroll finalisation.</p>
        </div>
        <div className="on-call-actions">
          <Link className="on-call-btn secondary" to="/on-call">View Rota</Link>
          {/* Managers read payroll and keep the salary figures up to date
              (Gareth 2026-09-17); team leaders run the rota but never see pay. */}
          {['admin', 'manager'].includes(role) && <Link className="on-call-btn secondary" to="/on-call/payroll">Payroll Reports</Link>}
        </div>
      </div>

      {/* A message is a string or { type: 'error', text }: rendering the
          object itself is a React crash that blanked the page. */}
      {message && <div className={message.type === 'error' ? 'admin-error' : 'admin-info'}>{message.text || message}</div>}

      <div className="on-call-planner-layout">
        <section className="on-call-card planner-main">
          <div className="planner-toolbar">
            <div>
              <h2>{MONTHS[month - 1]} {year}</h2>
              <p className="on-call-muted">Rota weeks run Friday to Thursday. Click a day to edit Main Cover and Scotland Cover together.</p>
            </div>
            <div className="on-call-controls">
              <label>Year
                <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value, 10) || new Date().getFullYear())} />
              </label>
              <label>Month
                <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))}>
                  {MONTHS.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
                </select>
              </label>
              <button className="on-call-btn secondary" onClick={createBlankYear}>Create blank year</button>
            </div>
          </div>

          {rota?.warnings?.length > 0 && (
            <div className="on-call-warning-panel compact">
              <strong>{rota.warnings.length} day(s) need attention</strong>
              <span>Missing cover or duplicate Main/Scotland engineer.</span>
            </div>
          )}

          {/* What each colour means; editing a type's colour below repaints
              the calendar as soon as it is saved. */}
          {(rota?.cover_types || []).length > 0 && (
            <div className="cover-legend" aria-label="Cover types">
              {rota.cover_types.map(type => (
                <span key={type.cover_type_id} className="cover-legend-item">
                  {isCoverColour(type.color) && <span className="cover-type-swatch" style={{ background: type.color }} />}
                  {type.label}
                </span>
              ))}
            </div>
          )}

          <div className="admin-rota-grid">
            {WEEKDAYS.map(day => <div key={day} className="admin-rota-weekday">{day}</div>)}
            {getCalendarRange(year, month).days.map(day => {
              const key = dateKey(day);
              const assignments = daysByDate[key] || [];
              const warnings = warningsByDate[key] || [];
              const outside = day.getUTCMonth() !== month - 1;
              return (
                <button
                  key={key}
                  className={`admin-rota-day ${outside ? 'outside' : ''} ${warnings.length ? 'has-warning' : ''}`}
                  onClick={() => !outside && openDayEditor(day)}
                  disabled={outside}
                  title={warnings.map(w => w.message).join('; ') || 'Edit day'}
                >
                  <div className="admin-rota-day-top">
                    <strong>{day.getUTCDate()}</strong>
                    {warnings.length > 0 && <span className="warning-dot">!</span>}
                  </div>
                  <div className="admin-rota-cover-list">
                    {assignments.map(item => (
                      <div
                        key={item.assignment_id}
                        className={`admin-rota-cover ${item.engineer_id ? '' : 'missing'}`}
                        style={coverTypeStyle(item.cover_type_color, { assigned: Boolean(item.engineer_id) })}
                      >
                        <span>{item.cover_type_label}{item.cover_type_active === false ? ' (retired)' : ''}</span>
                        <strong>{item.engineer_name || 'Unassigned'}</strong>
                      </div>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <aside className="planner-sidebar">
          <section className="on-call-card">
            <h2>Block assignment</h2>
            <form className="planner-form" onSubmit={applyBlockAssignment}>
              <label>Start date
                <input type="date" value={blockForm.start_date} onChange={e => setBlockForm(prev => ({ ...prev, start_date: e.target.value }))} required />
              </label>
              <label>End date
                <input type="date" value={blockForm.end_date} onChange={e => setBlockForm(prev => ({ ...prev, end_date: e.target.value }))} required />
              </label>
              <label>Cover type
                <select value={blockForm.cover_type_id} onChange={e => setBlockForm(prev => ({ ...prev, cover_type_id: e.target.value }))} required>
                  {coverTypes.filter(type => type.is_active !== false).map(type => <option key={type.cover_type_id} value={type.cover_type_id}>{type.label}</option>)}
                </select>
              </label>
              <label>Engineer
                <select value={blockForm.engineer_id} onChange={e => setBlockForm(prev => ({ ...prev, engineer_id: e.target.value }))}>
                  <option value="">Unassigned</option>
                  {engineers.map(engineer => <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>)}
                </select>
              </label>
              <label>Notes
                <textarea rows={3} value={blockForm.notes} onChange={e => setBlockForm(prev => ({ ...prev, notes: e.target.value }))} />
              </label>
              <button className="on-call-btn primary" type="submit">Apply Block</button>
            </form>
          </section>

        </aside>
      </div>

      <AdminOperations
        gapStart={gapStart}
        setGapStart={setGapStart}
        gapEnd={gapEnd}
        setGapEnd={setGapEnd}
        gapWarnings={gapWarnings}
        reviewGaps={reviewGaps}
        swapRequests={swapRequests}
        reviewSwap={reviewSwap}
        auditLog={auditLog}
        auditTotal={auditTotal}
        loadMoreAudit={loadMoreAudit}
        coverTypes={coverTypes}
        coverForm={coverForm}
        setCoverForm={setCoverForm}
        createCoverType={createCoverType}
        saveCoverType={saveCoverType}
        setCoverTypeActive={setCoverTypeActive}
        engineers={engineers}
        assignedSwapOptions={assignedSwapOptions}
        adminSwapForm={adminSwapForm}
        setAdminSwapForm={setAdminSwapForm}
        selectedAdminSwapAssignment={selectedAdminSwapAssignment}
        savingAdminSwap={savingAdminSwap}
        createAdminSwapRequest={createAdminSwapRequest}
      />

      {editingDay && (
        <div className="on-call-modal-backdrop" onClick={() => setEditingDay(null)}>
          <div className="on-call-modal day-editor-modal" onClick={event => event.stopPropagation()}>
            <h2>Edit {editingDay.date}</h2>
            {(warningsByDate[editingDay.date] || []).length > 0 && (
              <div className="on-call-warning-panel">
                {(warningsByDate[editingDay.date] || []).map(w => <div key={w.message}>{w.message}</div>)}
              </div>
            )}
            <div className="day-editor-grid">
              {editingDay.assignments.map(assignment => (
                <div key={assignment.assignment_id} className="day-editor-cover">
                  <h3>
                    {isCoverColour(assignment.cover_type_color) && <span className="cover-type-swatch" style={{ background: assignment.cover_type_color }} />}
                    {assignment.cover_type_label}{assignment.cover_type_active === false ? ' (retired)' : ''}
                  </h3>
                  <label>Engineer
                    <select
                      value={dayForm[assignment.assignment_id]?.engineer_id || ''}
                      onChange={e => setDayForm(prev => ({
                        ...prev,
                        [assignment.assignment_id]: { ...prev[assignment.assignment_id], engineer_id: e.target.value }
                      }))}
                    >
                      <option value="">Unassigned</option>
                      {engineers.map(engineer => <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>)}
                    </select>
                  </label>
                  <label>Notes
                    <textarea
                      rows={3}
                      value={dayForm[assignment.assignment_id]?.notes || ''}
                      onChange={e => setDayForm(prev => ({
                        ...prev,
                        [assignment.assignment_id]: { ...prev[assignment.assignment_id], notes: e.target.value }
                      }))}
                    />
                  </label>
                </div>
              ))}
            </div>
            <div className="on-call-actions">
              <button className="on-call-btn secondary" onClick={() => setEditingDay(null)}>Cancel</button>
              <button className="on-call-btn primary" onClick={saveDayEditor}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminOperations(props) {
  const {
    gapStart, setGapStart, gapEnd, setGapEnd, gapWarnings, reviewGaps,
    swapRequests, reviewSwap, auditLog, auditTotal, loadMoreAudit, coverTypes, coverForm, setCoverForm, createCoverType,
    saveCoverType, setCoverTypeActive,
    engineers, assignedSwapOptions, adminSwapForm, setAdminSwapForm,
    selectedAdminSwapAssignment, savingAdminSwap, createAdminSwapRequest
  } = props;

  return (
    <>
      <div className="on-call-card">
        <h2>Rota gap review</h2>
        <div className="on-call-form-row">
          <label>Start
            <input type="date" value={gapStart} onChange={e => setGapStart(e.target.value)} />
          </label>
          <label>End
            <input type="date" value={gapEnd} onChange={e => setGapEnd(e.target.value)} />
          </label>
          <button className="on-call-btn secondary" onClick={() => reviewGaps(false)}>Review gaps</button>
          <button className="on-call-btn primary" onClick={() => reviewGaps(true)}>Notify Management/Scheduling</button>
        </div>
        {gapWarnings.length > 0 && (
          <div className="on-call-warning-list">
            {gapWarnings.map(day => <div key={day.date}>{day.date}: {day.warnings.map(w => w.message).join('; ')}</div>)}
          </div>
        )}
      </div>

      <div className="on-call-card">
        <h2>Pending swap requests</h2>
        {swapRequests.length === 0 ? <p className="on-call-muted">No pending swap requests.</p> : (
          <table className="on-call-table">
            <thead><tr><th>Date</th><th>Cover</th><th>From</th><th>Proposed</th><th>Reason</th><th>Actions</th></tr></thead>
            <tbody>
              {swapRequests.map(request => (
                <tr key={request.request_id}>
                  <td>{String(request.rota_date).slice(0, 10)}</td>
                  <td>{request.cover_type_label}</td>
                  <td>{request.current_engineer_name}</td>
                  <td>{request.proposed_engineer_name}</td>
                  <td>{request.reason}</td>
                  <td>
                    <button className="on-call-btn primary" onClick={() => reviewSwap(request, 'approved')}>Approve</button>
                    <button className="on-call-btn danger" onClick={() => reviewSwap(request, 'rejected')}>Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <CoverTypesCard
        coverTypes={coverTypes}
        coverForm={coverForm}
        setCoverForm={setCoverForm}
        createCoverType={createCoverType}
        saveCoverType={saveCoverType}
        setCoverTypeActive={setCoverTypeActive}
      />

      <div className="on-call-card">
        <h2>Create swap request</h2>
        <p className="on-call-muted">Admin-only test/operations tool. The request still needs normal approval.</p>
        <form className="planner-form admin-swap-request-form" onSubmit={createAdminSwapRequest}>
          <label>Current assignment
            <select
              value={adminSwapForm.assignment_id}
              onChange={e => setAdminSwapForm(prev => ({ ...prev, assignment_id: e.target.value, proposed_engineer_id: '' }))}
              required
            >
              <option value="">Select assigned cover</option>
              {assignedSwapOptions.map(option => (
                <option key={option.assignment_id} value={option.assignment_id}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>Proposed replacement
            <select
              value={adminSwapForm.proposed_engineer_id}
              onChange={e => setAdminSwapForm(prev => ({ ...prev, proposed_engineer_id: e.target.value }))}
              required
              disabled={!adminSwapForm.assignment_id}
            >
              <option value="">Select replacement engineer</option>
              {engineers
                .filter(engineer => engineer.user_id !== selectedAdminSwapAssignment?.engineer_id)
                .map(engineer => <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>)}
            </select>
          </label>
          <label>Reason
            <textarea
              rows={3}
              value={adminSwapForm.reason}
              onChange={e => setAdminSwapForm(prev => ({ ...prev, reason: e.target.value }))}
              required
              placeholder="Why this swap is being raised"
            />
          </label>
          <button className="on-call-btn primary" type="submit" disabled={savingAdminSwap || assignedSwapOptions.length === 0}>
            {savingAdminSwap ? 'Creating...' : 'Create Request'}
          </button>
        </form>
      </div>

      <div className="on-call-card">
        <h2>Rota audit log</h2>
        <table className="on-call-table">
          <thead><tr><th>Date</th><th>Cover</th><th>Previous</th><th>New</th><th>Changed by</th><th>Source</th><th>Note</th></tr></thead>
          <tbody>
            {auditLog.map(entry => (
              <tr key={entry.audit_id}>
                <td>{String(entry.rota_date).slice(0, 10)}</td>
                <td>{entry.cover_type_label}</td>
                <td>{entry.previous_engineer_name || '-'}</td>
                <td>{entry.new_engineer_name || '-'}</td>
                <td>{entry.changed_by_name || '-'}</td>
                <td>{String(entry.change_source).replace(/_/g, ' ')}</td>
                <td>{entry.reason_note || '-'}</td>
              </tr>
            ))}
            {auditLog.length === 0 && <tr><td colSpan="7">No rota changes logged yet.</td></tr>}
          </tbody>
        </table>
        {auditTotal > auditLog.length && (
          <button className="on-call-btn secondary" style={{ marginTop: '12px' }} onClick={loadMoreAudit}>
            Load more ({auditLog.length} of {auditTotal})
          </button>
        )}
      </div>
    </>
  );
}

// Every type is listed, retired ones included. Label, colour and order edit
// in the row; the code never does (the rate seeding and the block-form
// default look it up by name). Retire is a soft flag - the type's assigned
// days keep their cover and pay - so it asks first and quotes how many
// future days are still on it. Reactivate needs no confirm.
function CoverTypesCard({ coverTypes, coverForm, setCoverForm, createCoverType, saveCoverType, setCoverTypeActive }) {
  const { confirmElement, requestConfirm } = useConfirm();
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', color: '#1D9E75', display_order: 100 });
  // The API refuses this too (409); disabling the button just says why first.
  const activeCount = coverTypes.filter(type => type.is_active !== false).length;

  const startEdit = (type) => {
    setEditingId(type.cover_type_id);
    setEditForm({ label: type.label, color: type.color || '#1D9E75', display_order: type.display_order ?? 100 });
  };

  const saveEdit = async (type) => {
    // A blanked order keeps the row's current value rather than sending ''.
    const order = parseInt(editForm.display_order, 10);
    const saved = await saveCoverType(type.cover_type_id, {
      label: editForm.label.trim(),
      color: editForm.color,
      display_order: Number.isNaN(order) ? type.display_order : order,
      is_active: type.is_active !== false
    }, 'Cover type updated');
    if (saved) setEditingId(null);
  };

  const futureDaysText = (type) => {
    const count = type.future_assignment_count || 0;
    if (count === 0) return `No future rota days use ${type.label}. Retiring only stops new cover being planned on it.`;
    return `${type.label} has ${count} future rota day${count === 1 ? '' : 's'} from today with an engineer on it. `
      + `Those days keep their cover and standby pay; retiring only stops new cover being planned on ${type.label}.`;
  };

  const confirmRetire = (type) => requestConfirm({
    title: `Retire ${type.label}?`,
    message: futureDaysText(type),
    confirmLabel: 'Retire',
    danger: true,
    onConfirm: () => setCoverTypeActive(type, false)
  });

  return (
    <div className="on-call-card">
      <h2>Cover types</h2>
      <table className="on-call-table cover-types-table">
        <thead><tr><th>Label</th><th>Code</th><th>Colour</th><th>Order</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {coverTypes.map(type => {
            const retired = type.is_active === false;
            if (editingId === type.cover_type_id) {
              return (
                <tr key={type.cover_type_id}>
                  <td><input className="cover-type-label-input" aria-label="Label" value={editForm.label} onChange={e => setEditForm(prev => ({ ...prev, label: e.target.value }))} required /></td>
                  <td>{type.code}</td>
                  <td><input type="color" aria-label="Colour" value={editForm.color} onChange={e => setEditForm(prev => ({ ...prev, color: e.target.value }))} /></td>
                  <td><input type="number" min="0" aria-label="Order" value={editForm.display_order} onChange={e => setEditForm(prev => ({ ...prev, display_order: e.target.value }))} /></td>
                  <td>{retired ? 'Retired' : 'Active'}</td>
                  <td>
                    <button className="on-call-btn primary" type="button" onClick={() => saveEdit(type)} disabled={!editForm.label.trim()}>Save</button>
                    <button className="on-call-btn secondary" type="button" onClick={() => setEditingId(null)}>Cancel</button>
                  </td>
                </tr>
              );
            }
            return (
              <tr key={type.cover_type_id} className={retired ? 'cover-type-retired' : ''}>
                <td>{type.label}</td>
                <td>{type.code}</td>
                <td><span className="cover-type-swatch" style={{ background: type.color }} />{type.color}</td>
                <td>{type.display_order}</td>
                <td>{retired ? 'Retired' : 'Active'}</td>
                <td>
                  <button className="on-call-btn secondary" type="button" aria-label={`Edit ${type.label}`} onClick={() => startEdit(type)}>Edit</button>
                  {retired ? (
                    <button className="on-call-btn secondary" type="button" aria-label={`Reactivate ${type.label}`} onClick={() => setCoverTypeActive(type, true)}>Reactivate</button>
                  ) : (
                    <button
                      className="on-call-btn danger"
                      type="button"
                      aria-label={`Retire ${type.label}`}
                      disabled={activeCount <= 1}
                      title={activeCount <= 1 ? 'The rota needs at least one active cover type' : undefined}
                      onClick={() => confirmRetire(type)}
                    >
                      Retire
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <form className="on-call-form-row" onSubmit={createCoverType}>
        <label>New cover type
          <input value={coverForm.label} onChange={e => setCoverForm(prev => ({ ...prev, label: e.target.value }))} required />
        </label>
        <label>Colour
          <input type="color" value={coverForm.color} onChange={e => setCoverForm(prev => ({ ...prev, color: e.target.value }))} />
        </label>
        <label>Order
          <input type="number" value={coverForm.display_order} onChange={e => setCoverForm(prev => ({ ...prev, display_order: parseInt(e.target.value, 10) || 100 }))} />
        </label>
        <button className="on-call-btn primary" type="submit">Add cover type</button>
      </form>
      {confirmElement}
    </div>
  );
}

export default OnCallAdmin;
